'use strict';

import size from 'lodash/size';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import cloudinary from 'cloudinary-core';
import React from 'react';
import ReactDOM from 'react-dom';
import Arg from 'arg';
import moment from 'moment'; // eslint-disable-line no-unused-vars
import Sentry from 'sentry';
import SHCommonModules from 'sh-common-modules'; // eslint-disable-line no-unused-vars
import SHGlobals from 'sh-globals';
import SHPolyfills from 'sh-polyfills';
import SHSignals from 'sh-signals';
import SHNotifications from 'sh-notifications';
import APIUtils, { APIRegistry } from '@spothero/utils/api';
import DOMUtils from '@spothero/utils/dom';
import SegmentUtils from 'utils/segment';
import StorageUtils from 'storage-utils';
import GDPRDialog from 'global/components/common/utils/GDPRDialog';

const promoCodeNotificationDataReceived = function promoCodeNotificationDataReceived(data) {
    const msg = data.message.replace('*|PROMOCODE|*', data.promoCode);

    SHNotifications.add(
        {
            message: msg,
            autoClose: false,
            cookie: {
                name: 'spothero-' + data.promoCode + '-promoCodeNotification'
            },
            buttons: [
                {
                    displayClass: 'Button-link',
                    clickClass: 'btn-promo-notification',
                    label: 'Use Promo Code Now',
                    callback: applyNotificationPromoCode,
                    callbackParams: [data.promoCode]
                }
            ]
        }
    );
};

const applyNotificationPromoCode = function applyNotificationPromoCode(promoCode) {
    const query = (size(Arg.query())) ? '&promo=' : '?promo=';

    StorageUtils.set('spothero-promoCodeNotificationSuccess', '1');

    window.location = window.location.href + query + promoCode;
};

const removeProtocol = function removeProtocol(href) {
    return href.replace(/.*?:\/\//g, '');
};

const getMediaSource = () => {
    if (document.referrer.search('https?://(.*)google.([^/?]*)') === 0) {
        return 'Google';
    } else if (document.referrer.search('https?://(.*)bing.([^/?]*)') === 0) {
        return 'Bing';
    } else if (document.referrer.search('https?://(.*)yahoo.([^/?]*)') === 0) {
        return 'Yahoo';
    } else if (document.referrer.search('https?://(.*)facebook.([^/?]*)') === 0) {
        return 'Facebook';
    } else if (document.referrer.search('https?://(.*)twitter.([^/?]*)') === 0) {
        return 'Twitter';
    } else {
        return 'Other';
    }
};

const segmentInitOnLoad = () => {
    if (window.spotHeroUser) {
        const segmentTraits = {
            'media source': getMediaSource(),
            'user id': window.spotHeroUser.id,
            email: window.spotHeroUser.email,
            created: window.spotHeroUser.created,
            name: window.spotHeroUser.full_name,
            'new user': window.spotHeroUser.isNewUser,
            'referrer code': window.spotHeroUser.referralToken
        };

        SegmentUtils.identify({
            uniqueId: window.spotHeroUser.id,
            traits: segmentTraits
        });
    }
};

module.exports = window.SpotHero = {
    init() {
        const {
            email,
            id,
            admin: isAdmin,
            is_seller: isSeller
        } = window.spotHeroUser;
        const status = (id)
            ? 'user'
            : (email)
                ? 'guest'
                : 'logged-out';
        // new, non-jQuery dependent cloudinary lib initialization
        const cl = cloudinary.Cloudinary.new({ cloud_name: window.CLOUDINARY_CLOUD_NAME });
        // Record landing page
        const landingPage = StorageUtils.get('spothero-landing-page-source');
        const navContainer = document.querySelector('.navbar');
        const footerContainer = document.getElementById('Footer-container');

        SHGlobals.init({
            cloudinary: cl
        });

        SHPolyfills.init();

        APIUtils.init({
            baseUrl: SHGlobals.API_PATH,
            xsrfCookieName: 'csrftoken',
            xsrfHeaderName: 'X-CSRFToken',
            additionalHeaders: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });

        // We need an absolute url for retry functionality
        const port = (window.location.port) ? `:${window.location.port}` : '';
        const prefix = `${window.location.protocol}//${window.location.hostname}${port}`;
        const searchAPIUtils = APIRegistry.create(
            {
                name: 'ratesSearch',
                config: {
                    baseUrl: `${prefix}${SHGlobals.API_PATH}`,
                    additionalHeaders: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                }
            });
        searchAPIUtils.addRetry({
            retries: 10
        });

        // GDPR
        if (SHGlobals.cloudfrontViewerCountryCode
            && SHGlobals.cloudfrontViewerCountryCode !== 'US'
            && SHGlobals.cloudfrontViewerCountryCode !== 'CA'
        ) {
            setTimeout(() => {
                ReactDOM.render(
                    <GDPRDialog
                        onHidden={() => {
                            const dialogElement = DOMUtils.el('.GDPRDialog');
                            dialogElement.parentElement.removeChild(dialogElement);
                        }}
                        countryCode={SHGlobals.cloudfrontViewerCountryCode}
                    />,
                    document.getElementById('gdpr-dialog-container')
                );
            }, 0);
        }

        if (SHGlobals.isSegmentEnabled) {
            SegmentUtils.init({
                apiKey: SHGlobals.segmentApiKey,
                onLoad: segmentInitOnLoad,
                debug: !SHGlobals.isDeployed
            });
        }

        if (SHGlobals.isProduction) {
            Sentry.init({
                apiKey: window.sentryAPIPublicKey,
                additionalRavenOptions: {
                    ignoreUrls: [
                        /d2yt77z1f5bzb8\.cloudfront\.net\/static\/control_panel\/js/i
                    ]
                },
                isAuthenticated: window.spotHeroUser,
                email,
                id
            });
        }

        if (window.shTestInfo.isRunningTest) {
            window.TestUtils = require('react-dom/test-utils');
        }

        if (StorageUtils.get('spothero-promoCodeNotificationSuccess')) {
            SHNotifications.add(
                {
                    type: 'success',
                    message: 'The promo code will be applied when you book your spot.'
                }
            );

            StorageUtils.remove('spothero-promoCodeNotificationSuccess');
        }

        if (includes(window.location.search, 'signed-in')) {
            SHNotifications.add(
                {
                    type: 'success',
                    message: 'Signed in successfully.'
                }
            );
        }

        // add referrer cookie if present in URL
        if (Arg.query().referrer) {
            StorageUtils.set('sh-referrer', Arg.query().referrer, 'cookie', 7);
        }

        SHSignals.promoCodeNotificationDataReceived.add(promoCodeNotificationDataReceived);

        if (isEmpty(landingPage)) {
            StorageUtils.set('spothero-landing-page-source', removeProtocol(document.URL));
        }

    }
};



// WEBPACK FOOTER //
// ./spothero/static/main/js/modules/common/spothero-base.js